/**
 * Reversal Engine — the inverse-action compiler behind "every write is
 * reversible".
 *
 * Given a *sealed* ledger entry (the action that ran + the captured
 * before-state), `invertAction` compiles the COMPENSATING action:
 *
 *   create → delete            (delete the record the create produced)
 *   update → restore prior     (re-apply the sealed before-state fields)
 *   delete → recreate          (rebuild the record from its snapshot)
 *
 * The result is ALWAYS an `ActionProposal` — never a direct write. A
 * reversal is just another governed proposal: it re-enters the SAME
 * approvals pipeline (queueApproval → operator sign-off → executor) as
 * every other agent write, so undo inherits the audit trail, the
 * idempotency fence and the activity ledger for free. No new governance
 * code exists here, by design.
 *
 * Irreversibility is a first-class, *explained* state: `canInvert` returns
 * an explicit human-readable reason for entries that cannot be undone
 * (emails/external sends left the building; an entry already reversed;
 * an entry that is itself a reversal), so the UI surfaces WHY instead of
 * a greyed-out mystery button.
 *
 * Everything in this module is pure — it consumes ledger entries and
 * before-state passed in, and never touches the store or Firebase.
 */

import type { AgentAction } from './types';

// --- Types ------------------------------------------------------------------

/** Reversal-only kind: delete the record a governed create produced. */
export interface DeleteRecordAction {
  kind: 'delete_record';
  recordType: 'person' | 'ticket' | 'task' | 'document';
  recordId: string;
  recordName: string;
}

/** Reversal-only kind: rebuild a deleted record from its sealed snapshot. */
export interface RecreateRecordAction {
  kind: 'recreate_record';
  recordType: 'person' | 'ticket' | 'task' | 'document';
  recordId: string;
  recordName: string;
  /** The full before-state snapshot captured when the record was deleted. */
  snapshot: Record<string, unknown>;
}

/** Any action a reversal can compile to: native agent actions + the two above. */
export type ReversalAction = AgentAction | DeleteRecordAction | RecreateRecordAction;

/**
 * Every action type the ledger can carry. Native agent kinds, the two
 * reversal-only kinds, and the irreversible external side-effect kinds
 * (an email that was sent cannot be unsent — but it CAN be explained).
 */
export type ActionType =
  | AgentAction['kind']
  | 'delete_record'
  | 'recreate_record'
  | 'send_email'
  | 'external_send';

/**
 * A sealed ledger entry: what ran, against which record, and the captured
 * before-state. This is the reversal engine's only input shape — callers
 * (UI, tests) construct it from whatever ledger they hold.
 */
export interface LedgerEntry {
  id: string;
  /** ISO-8601 timestamp when the write executed. */
  at: string;
  actionType: ActionType;
  /** The sealed action, when the entry carries a native agent action. */
  action?: AgentAction;
  /** Primary record the write touched ('' when it was never captured). */
  recordId: string;
  recordName?: string;
  /** Captured before-state field values; absent for creates. */
  before?: Record<string, unknown> | null;
  /** Sealed old→new line (from `diffForAction`) when the write was an update. */
  delta?: string | null;
  /** Set when this entry is itself a reversal of another entry. */
  reversesEntryId?: string;
  /** Set once a reversal targeting this entry exists — blocks double-undo. */
  reversedByEntryId?: string;
}

/** Why an entry cannot be inverted. Discriminated from ActionProposal by `ok`. */
export interface InvertError {
  ok: false;
  entryId: string;
  reason: string;
}

/**
 * A compiled compensating proposal. NOT a write: the caller must submit it
 * into the existing approval loop (queueApproval) where it is approved or
 * dismissed like any other agent proposal.
 */
export interface ActionProposal {
  /** Deterministic id so a double-click compiles the same proposal. */
  id: string;
  action: ReversalAction;
  origin: 'reversal';
  /** The ledger entry this proposal compensates. */
  reversesEntryId: string;
  /** Marker note for the approvals queue (parseable — see REVERSAL_NOTE_PREFIX). */
  note: string;
  status: 'pending';
}

/** One row of the "what changed since" blast-radius preview. */
export interface BlastRadiusItem {
  entryId: string;
  recordId: string;
  summary: string;
}

// --- Reversibility registry --------------------------------------------------

/** Action types the compiler can produce an inverse for. */
export const SUPPORTED_INVERSE_TYPES: ActionType[] = [
  'create_person',
  'create_document',
  'create_ticket',
  'create_task',
  'set_prospect_stage',
  'set_customer_status',
  'move_workflow_stage',
  'set_ticket_status',
  'assign_ticket',
  'schedule_follow_up',
  'update_brain',
  'delete_record',
  'recreate_record',
];

/**
 * Irreversible types with the explicit human-readable reason the UI shows.
 * These are honest walls, not missing features.
 */
export const IRREVERSIBLE_REASONS: Partial<Record<ActionType, string>> = {
  send_email:
    'This email has already been sent — it left the system and cannot be unsent. ' +
    'Consider a follow-up correction instead.',
  external_send:
    'This action pushed data to an external system — Argo cannot recall what ' +
    'another system already received. Reverse it at the destination.',
  log_activity:
    'Activity log entries are append-only history — removing one would rewrite ' +
    'the timeline rather than undo a state change.',
};

/** Update kinds whose inverse needs the captured before-state to restore. */
const REQUIRES_BEFORE: ReadonlySet<ActionType> = new Set<ActionType>([
  'set_prospect_stage',
  'set_customer_status',
  'set_ticket_status',
  'schedule_follow_up',
  'update_brain',
  'delete_record',
]);

/** Narrow a compiled ReversalAction back to the native AgentAction union. */
export function isNativeAgentAction(action: ReversalAction): action is AgentAction {
  return action.kind !== 'delete_record' && action.kind !== 'recreate_record';
}

/** Discriminate invertAction's error branch. */
export function isInvertError(v: ActionProposal | InvertError): v is InvertError {
  return typeof v === 'object' && v !== null && (v as InvertError).ok === false;
}

// --- canInvert ----------------------------------------------------------------

/**
 * Guard: can this sealed entry be inverted? Returns `ok: false` with an
 * explicit reason for every irreversible case — the reason IS the product
 * surface ("reversibility, proven & felt"), never a silent disable.
 */
export function canInvert(entry: LedgerEntry): { ok: boolean; reason?: string } {
  if (entry.reversesEntryId) {
    return {
      ok: false,
      reason:
        `This entry is itself a reversal (of ${entry.reversesEntryId}). ` +
        'Undoing an undo would just re-apply the original change — re-propose it instead.',
    };
  }
  if (entry.reversedByEntryId) {
    return {
      ok: false,
      reason:
        `Already reversed by entry ${entry.reversedByEntryId}. ` +
        'A second undo would double-apply the compensation.',
    };
  }
  const irreversible = IRREVERSIBLE_REASONS[entry.actionType];
  if (irreversible) return { ok: false, reason: irreversible };
  if (!SUPPORTED_INVERSE_TYPES.includes(entry.actionType)) {
    return {
      ok: false,
      reason: `No inverse compiler is registered for “${entry.actionType}”.`,
    };
  }
  if (!entry.recordId && isCreateType(entry.actionType)) {
    return {
      ok: false,
      reason:
        'The id of the created record was not captured on this ledger entry, ' +
        'so a compensating delete cannot address it.',
    };
  }
  if (REQUIRES_BEFORE.has(entry.actionType) && !entry.before) {
    return {
      ok: false,
      reason:
        'No before-state was captured for this write, so the prior field values ' +
        'cannot be restored. Newer entries seal the before-state automatically.',
    };
  }
  return { ok: true };
}

function isCreateType(t: ActionType): boolean {
  return (
    t === 'create_person' ||
    t === 'create_document' ||
    t === 'create_ticket' ||
    t === 'create_task'
  );
}

// --- invertAction ---------------------------------------------------------------

/** Marker prefix used in the approvals-queue note so reversals are identifiable. */
export const REVERSAL_NOTE_PREFIX = '⟲ reverses ';

function proposal(entry: LedgerEntry, action: ReversalAction, what: string): ActionProposal {
  return {
    id: `undo-${entry.id}`,
    action,
    origin: 'reversal',
    reversesEntryId: entry.id,
    note: `${REVERSAL_NOTE_PREFIX}${entry.id} — ${what}`,
    status: 'pending',
  };
}

function fail(entry: LedgerEntry, reason: string): InvertError {
  return { ok: false, entryId: entry.id, reason };
}

/**
 * Compile the compensating proposal for a sealed entry. Pure: never writes.
 * The returned proposal must be submitted through the EXISTING approval
 * pipeline (queueApproval) — reversal is just another governed proposal.
 */
export function invertAction(entry: LedgerEntry): ActionProposal | InvertError {
  const guard = canInvert(entry);
  if (!guard.ok) return fail(entry, guard.reason ?? 'This entry cannot be inverted.');

  const before = entry.before ?? {};
  const a = entry.action;

  switch (entry.actionType) {
    // --- creates → delete ---------------------------------------------------
    case 'create_person':
      return proposal(
        entry,
        {
          kind: 'delete_record',
          recordType: 'person',
          recordId: entry.recordId,
          recordName: entry.recordName ?? entry.recordId,
        },
        `delete created contact ${entry.recordName ?? entry.recordId}`,
      );
    case 'create_ticket':
      return proposal(
        entry,
        {
          kind: 'delete_record',
          recordType: 'ticket',
          recordId: entry.recordId,
          recordName: entry.recordName ?? entry.recordId,
        },
        `delete created ticket ${entry.recordName ?? entry.recordId}`,
      );
    case 'create_task':
      return proposal(
        entry,
        {
          kind: 'delete_record',
          recordType: 'task',
          recordId: entry.recordId,
          recordName: entry.recordName ?? entry.recordId,
        },
        `delete created task ${entry.recordName ?? entry.recordId}`,
      );
    case 'create_document':
      return proposal(
        entry,
        {
          kind: 'delete_record',
          recordType: 'document',
          recordId: entry.recordId,
          recordName: entry.recordName ?? entry.recordId,
        },
        `delete created document ${entry.recordName ?? entry.recordId}`,
      );

    // --- delete → recreate ---------------------------------------------------
    case 'delete_record': {
      const snapshot = entry.before as Record<string, unknown>;
      const recordType =
        (snapshot['recordType'] as RecreateRecordAction['recordType'] | undefined) ??
        'person';
      return proposal(
        entry,
        {
          kind: 'recreate_record',
          recordType,
          recordId: entry.recordId,
          recordName: entry.recordName ?? entry.recordId,
          snapshot,
        },
        `recreate deleted record ${entry.recordName ?? entry.recordId}`,
      );
    }
    case 'recreate_record':
      return proposal(
        entry,
        {
          kind: 'delete_record',
          recordType: 'person',
          recordId: entry.recordId,
          recordName: entry.recordName ?? entry.recordId,
        },
        `delete recreated record ${entry.recordName ?? entry.recordId}`,
      );

    // --- updates → restore prior fields --------------------------------------
    case 'set_prospect_stage': {
      if (a?.kind !== 'set_prospect_stage') return fail(entry, 'Sealed action missing.');
      const prior = before['stage'];
      if (typeof prior !== 'string') {
        return fail(entry, 'The before-state has no prior stage to restore.');
      }
      return proposal(
        entry,
        { ...a, stage: prior as typeof a.stage },
        `restore ${a.personName} to stage ${prior}`,
      );
    }
    case 'set_customer_status': {
      if (a?.kind !== 'set_customer_status') return fail(entry, 'Sealed action missing.');
      const prior = before['status'];
      if (typeof prior !== 'string') {
        return fail(entry, 'The before-state has no prior status to restore.');
      }
      return proposal(
        entry,
        { ...a, status: prior as typeof a.status },
        `restore ${a.personName} to status ${prior}`,
      );
    }
    case 'move_workflow_stage': {
      if (a?.kind !== 'move_workflow_stage') return fail(entry, 'Sealed action missing.');
      // The sealed action already carries the prior stage — swap direction.
      return proposal(
        entry,
        { ...a, fromStage: a.toStage, toStage: a.fromStage },
        `move “${a.workflowName}” back to ${a.fromStage}`,
      );
    }
    case 'set_ticket_status': {
      if (a?.kind !== 'set_ticket_status') return fail(entry, 'Sealed action missing.');
      const prior = before['status'];
      if (typeof prior !== 'string') {
        return fail(entry, 'The before-state has no prior ticket status to restore.');
      }
      return proposal(
        entry,
        { ...a, status: prior as typeof a.status },
        `restore ${a.ticketTitle} to ${prior}`,
      );
    }
    case 'assign_ticket': {
      if (a?.kind !== 'assign_ticket') return fail(entry, 'Sealed action missing.');
      const prior =
        (typeof before['owner'] === 'string' ? (before['owner'] as string) : undefined) ??
        a.fromOwner ??
        undefined;
      if (!prior) {
        return fail(
          entry,
          'The ticket had no owner before this assignment — “unowned” cannot be ' +
            'expressed as a reassignment. Clear the owner manually instead.',
        );
      }
      return proposal(
        entry,
        { ...a, owner: prior, fromOwner: a.owner },
        `hand ${a.ticketTitle} back to @${prior}`,
      );
    }
    case 'schedule_follow_up': {
      if (a?.kind !== 'schedule_follow_up') return fail(entry, 'Sealed action missing.');
      const priorDate = before['followUpDate'];
      if (typeof priorDate !== 'string' || !priorDate) {
        return fail(
          entry,
          'No follow-up existed before this write — clearing a follow-up cannot ' +
            'be expressed as a reschedule.',
        );
      }
      const priorNote = before['nextStep'];
      return proposal(
        entry,
        {
          ...a,
          followUpDate: priorDate,
          note: typeof priorNote === 'string' ? priorNote : undefined,
        },
        `restore ${a.personName}'s follow-up to ${priorDate}`,
      );
    }
    case 'update_brain': {
      if (a?.kind !== 'update_brain') return fail(entry, 'Sealed action missing.');
      const prior = before['value'];
      if (typeof prior !== 'string') {
        return fail(
          entry,
          'This Brain fact did not exist before the write — restoring it means ' +
            'removing the fact, which needs the Brain surface, not a re-write.',
        );
      }
      return proposal(
        entry,
        { ...a, value: prior },
        `restore ${a.label} to “${prior}”`,
      );
    }
    default:
      return fail(entry, `No inverse compiler is registered for “${entry.actionType}”.`);
  }
}

// --- forwardPatch (round-trip proof helper) -----------------------------------

/**
 * The field patch an action writes onto its target record — the pure
 * semantics of applying the action, used to PROVE the round-trip:
 * `{...after, ...forwardPatch(inverse)}` deep-equals the before-state.
 * Returns null for actions that don't patch an existing record.
 */
export function forwardPatch(action: ReversalAction): Record<string, unknown> | null {
  switch (action.kind) {
    case 'set_prospect_stage':
      return { stage: action.stage };
    case 'set_customer_status':
      return { status: action.status };
    case 'move_workflow_stage':
      return { stage: action.toStage };
    case 'set_ticket_status':
      return { status: action.status };
    case 'assign_ticket':
      return { owner: action.owner };
    case 'schedule_follow_up':
      return {
        followUpDate: action.followUpDate,
        nextStep: action.note,
      };
    case 'update_brain':
      return { value: action.value };
    default:
      return null;
  }
}

// --- blastRadius ---------------------------------------------------------------

/**
 * PURE forward-walk of the ledger from the target entry: every later sealed
 * write that touched the same record, in chronological order — the honest
 * "what changed since" preview shown before an undo is confirmed. Unrelated
 * records are excluded; the target entry itself is excluded.
 */
export function blastRadius(
  entry: LedgerEntry,
  laterEntries: LedgerEntry[],
): BlastRadiusItem[] {
  return laterEntries
    .filter(
      (e) =>
        e.id !== entry.id &&
        e.at > entry.at &&
        e.recordId !== '' &&
        e.recordId === entry.recordId,
    )
    .sort((x, y) => (x.at < y.at ? -1 : x.at > y.at ? 1 : 0))
    .map((e) => ({
      entryId: e.id,
      recordId: e.recordId,
      summary:
        e.delta ??
        `${e.actionType.replace(/_/g, ' ')} · ${e.recordName ?? e.recordId}`,
    }));
}

// --- Ledger adapters (pure mappers for the thin UI) ------------------------------

/**
 * Structural shape of an approvals-collection record — typed structurally so
 * this module (and its tests) never import the db layer.
 */
export interface ApprovalLike {
  id: string;
  createdAt: string;
  action: AgentAction;
  note?: string;
  status?: string;
}

/** Extract the primary record id a native action touches ('' when unknown). */
export function recordIdForAction(action: AgentAction): string {
  switch (action.kind) {
    case 'set_prospect_stage':
    case 'set_customer_status':
    case 'schedule_follow_up':
    case 'log_activity':
      return action.personId;
    case 'set_ticket_status':
    case 'assign_ticket':
      return action.ticketId;
    case 'move_workflow_stage':
      return action.accountId;
    case 'update_brain':
      return `${action.accountId}:${action.section}:${action.field}`;
    default:
      // Creates: the produced record's id is not known at proposal time.
      return '';
  }
}

/** Human name of the record a native action touches, for the preview rows. */
export function recordNameForAction(action: AgentAction): string | undefined {
  switch (action.kind) {
    case 'create_person':
      return action.name;
    case 'set_prospect_stage':
    case 'set_customer_status':
    case 'schedule_follow_up':
    case 'log_activity':
      return action.personName;
    case 'create_ticket':
      return action.title;
    case 'set_ticket_status':
    case 'assign_ticket':
      return action.ticketTitle;
    case 'move_workflow_stage':
      return action.accountName;
    case 'create_task':
      return action.title;
    case 'create_document':
      return action.entityName;
    case 'update_brain':
      return `${action.accountName} · ${action.label}`;
  }
}

/**
 * Map an approvals-collection record onto the reversal engine's ledger shape.
 * Best-effort and honest: before-state is synthesized ONLY where the sealed
 * action itself carries the prior value (`move_workflow_stage.fromStage`,
 * `assign_ticket.fromOwner`); everything else surfaces "no before-state"
 * through `canInvert` rather than guessing.
 */
export function entryFromApproval(item: ApprovalLike): LedgerEntry {
  const action = item.action;
  let before: Record<string, unknown> | undefined;
  if (action.kind === 'move_workflow_stage') before = { stage: action.fromStage };
  if (action.kind === 'assign_ticket' && action.fromOwner) {
    before = { owner: action.fromOwner };
  }
  const noteMatch =
    typeof item.note === 'string' && item.note.startsWith(REVERSAL_NOTE_PREFIX)
      ? item.note.slice(REVERSAL_NOTE_PREFIX.length).split(/\s/)[0]
      : undefined;
  return {
    id: item.id,
    at: item.createdAt,
    actionType: action.kind,
    action,
    recordId: recordIdForAction(action),
    recordName: recordNameForAction(action),
    before,
    reversesEntryId: noteMatch || undefined,
  };
}

/**
 * Cross-link reversals: for every entry that reverses another, stamp
 * `reversedByEntryId` on its target so `canInvert` blocks the double-undo.
 * Pure — returns new objects, input untouched.
 */
export function linkReversals(entries: LedgerEntry[]): LedgerEntry[] {
  const reversedBy = new Map<string, string>();
  for (const e of entries) {
    if (e.reversesEntryId) reversedBy.set(e.reversesEntryId, e.id);
  }
  return entries.map((e) =>
    reversedBy.has(e.id) ? { ...e, reversedByEntryId: reversedBy.get(e.id) } : e,
  );
}
